import type { CatalogCategory, CatalogSort } from "./catalog";
import type { ToolCategory } from "./types";

export interface CatalogView {
  path: string;
  category: CatalogCategory;
  query: string;
  sort: CatalogSort;
  page: number;
  pageSize: 12 | 24 | 48;
  scrollY: number;
}
const categories = new Set([
  "all",
  "favorites",
  "image",
  "pdf",
  "video",
  "audio",
  "table",
  "text",
  "data",
  "generator",
  "random",
  "daily",
  "developer",
  "security",
  "design",
  "network",
]);
const memory = new Map<string, CatalogView>();
export const CATALOG_STORAGE_KEY = "wyan.toolbox.catalog.v1";

export function normalizeCatalogView(
  value: unknown,
  path = "/tools",
  mobile = false,
): CatalogView {
  const fallbackCategory = path.startsWith("/tools/category/")
    ? (path.split("/").at(-1) as ToolCategory)
    : "all";
  const base: CatalogView = {
    path,
    category: categories.has(fallbackCategory) ? fallbackCategory : "all",
    query: "",
    sort: "smart",
    page: 1,
    pageSize: mobile ? 12 : 24,
    scrollY: 0,
  };
  if (
    !value ||
    typeof value !== "object" ||
    (value as CatalogView).path !== path
  )
    return base;
  const input = value as Partial<CatalogView>;
  return {
    ...base,
    category:
      path === "/tools" &&
      typeof input.category === "string" &&
      categories.has(input.category)
        ? input.category
        : base.category,
    query: typeof input.query === "string" ? input.query.slice(0, 120) : "",
    sort: ["smart", "name", "recent"].includes(input.sort || "")
      ? input.sort!
      : "smart",
    page:
      Number.isInteger(input.page) && input.page! > 0 && input.page! <= 1000
        ? input.page!
        : 1,
    pageSize: [12, 24, 48].includes(input.pageSize || 0)
      ? input.pageSize!
      : base.pageSize,
    scrollY:
      typeof input.scrollY === "number" && Number.isFinite(input.scrollY)
        ? Math.max(0, Math.min(100_000, input.scrollY))
        : 0,
  };
}

export function readCatalogView(
  path: string,
  historyValue?: unknown,
): CatalogView {
  const mobile = window.matchMedia("(max-width: 760px)").matches;
  if (historyValue && (historyValue as CatalogView).path === path)
    return normalizeCatalogView(historyValue, path, mobile);
  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(CATALOG_STORAGE_KEY) || "{}",
    );
    if (value && typeof value === "object" && Object.hasOwn(value, path))
      return normalizeCatalogView(value[path], path, mobile);
  } catch {
    /* Rejected storage falls back to the current page's memory. */
  }
  return normalizeCatalogView(memory.get(path), path, mobile);
}

export function writeCatalogView(view: CatalogView): void {
  const clean = normalizeCatalogView(view, view.path);
  memory.set(view.path, clean);
  try {
    let previous: Record<string, unknown> = {};
    try {
      previous = JSON.parse(
        window.sessionStorage.getItem(CATALOG_STORAGE_KEY) || "{}",
      );
    } catch {
      /* Replace malformed view state. */
    }
    const entries = Object.entries(
      previous && typeof previous === "object" ? previous : {},
    )
      .filter(
        ([path]) =>
          path === "/tools" || /^\/tools\/category\/[a-z]+$/.test(path),
      )
      .slice(-16);
    window.sessionStorage.setItem(
      CATALOG_STORAGE_KEY,
      JSON.stringify(Object.fromEntries([...entries, [view.path, clean]])),
    );
  } catch {
    /* Navigation still works without storage. */
  }
}
